import { useGetDealerBrandsQuery, useGetDealerShopTypesQuery } from '@api/customer.api';
import { DealerCheckbox } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button, useFindDealerContext } from '@components';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Checkbox,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
} from '@mui/material';
import Popper from '@mui/material/Popper';
import { t } from '@translations';
import { useState } from 'react';

export const FindDealerFilter = () => {
  const {
    currentData: shopTypes,
    isError: isShopTypesError,
    isLoading: isShopTypesLoading,
  } = useGetDealerShopTypesQuery();
  const {
    currentData: brands,
    isError: isBrandsError,
    isLoading: isBrandsLoading,
  } = useGetDealerBrandsQuery();

  const { baseDealerList, updateCurrentDealerList } = useFindDealerContext();

  const hasBrandsData = !isBrandsError && !isBrandsLoading && brands?.data;
  const hasShopTypesData = !isShopTypesError && !isShopTypesLoading && shopTypes?.data;
  const hasDealerData = baseDealerList && baseDealerList.length > 0;

  const [filter, setFilter] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? 'find-dealer-filter-popper' : undefined;

  const handleToggle = (value: string) => () => {
    const currentIndex = filter.indexOf(value);
    const newChecked = [...filter];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setFilter(newChecked);

    if (hasDealerData) {
      const matchingDealers = baseDealerList?.filter((dealer) => {
        const dealerBrands = dealer?.address?.flatMap((address) =>
          address.brandsTranslatedLabels.map((label) => label.label)
        );
        const dealerServices = dealer?.address?.flatMap((address) =>
          address.branchTypesTranslatedLabels.map((label) => label.label)
        );

        return newChecked.every(
          (val) => dealerBrands?.includes(val) || dealerServices?.includes(val)
        );
      });
      updateCurrentDealerList(matchingDealers);
    }
  };

  const clearFilters = () => {
    if (baseDealerList) {
      updateCurrentDealerList(baseDealerList);
      setFilter([]);
    }
  };

  const handleFilterToggle = () => {
    setOpen((previousOpen) => !previousOpen);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((previousOpen) => !previousOpen);
    setAnchorEl(event.currentTarget);
  };

  const createListItem = (item: DealerCheckbox) => {
    const labelId = `checkbox-list-label-${item.id}`;

    return (
      <ListItem key={item.id} dense onClick={handleToggle(item.label)}>
        <Checkbox
          edge='start'
          checked={filter.indexOf(item.label) !== -1}
          tabIndex={-1}
          disableRipple
          inputProps={{ 'aria-labelledby': labelId }}
        />
        <ListItemText id={labelId} primary={item.label} />
      </ListItem>
    );
  };

  return (
    <>
      <Button
        onClick={handleClick}
        onTouchEnd={handleClick}
        variant='tertiary'
        busy={isShopTypesLoading || isBrandsLoading}
        startIcon={<FilterAltIcon />}
        aria-describedby={id}
        sx={{ minWidth: '160px !important' }}
        disabled={!hasBrandsData && !hasShopTypesData && !hasDealerData}
      >
        {t(TextCodes.Dealer.Find.Filter)} {filter.length > 0 && `(${filter.length})`}
      </Button>
      <Popper id={id} open={open} anchorEl={anchorEl} placement='bottom-end'>
        <ClickAwayListener onClickAway={handleFilterToggle}>
          <Paper>
            <List>
              {hasBrandsData && (
                <>
                  <ListSubheader disableSticky>{t(TextCodes.Dealer.Find.Brands)}</ListSubheader>
                  {brands?.data?.map((item: DealerCheckbox) => createListItem(item))}
                </>
              )}
              {hasShopTypesData && (
                <>
                  <ListSubheader disableSticky>{t(TextCodes.Dealer.Find.Services)}</ListSubheader>
                  {shopTypes?.data?.map((item: DealerCheckbox) => createListItem(item))}
                </>
              )}
            </List>
            <Button onClick={clearFilters} disabled={!hasBrandsData && !hasShopTypesData}>
              {hasBrandsData || hasShopTypesData
                ? t(TextCodes.Dealer.Find.ClearFilter)
                : t(TextCodes.Dealer.Find.NoFilters)}
            </Button>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
